/**
 * Refactored StoreMap Component
 *
 * Clean, focused component using smaller sub-components
 * following Single Responsibility Principle and Clean Architecture
 */

import React, {useCallback, useMemo} from 'react';
import {useMapStore} from '../../../features/map/store/MapStore';
import {useStoreSelectionStore} from '../../../features/stores/store/StoreSelectionStore';
import {useMarketerSelectionStore} from '../../../features/marketers/store/MarketerSelectionStore';
import {useCoordinateValidation} from '../../../features/stores/hooks/useCoordinateValidation';
import {useMarketerControl} from '../../../features/marketers/hooks/useMarketerControl';
import {useMarketersMap} from '../../../features/marketers/hooks/useMarketersMap';
import {useAuth} from '../../../features/auth/hooks/useAuth';
import {AuthorizationService} from '../../../Services/authorization/AuthorizationService';
import {MapViewMode} from '../../../features/dashboard/types/viewMode.types';
import {Store} from '../../../features/stores/models/store.model';

// Import refactored components
import {
  MapErrorBoundary,
  MapView,
  MapControls,
  MapDataCounter,
  MapLoadingIndicator,
  StoreMarkers,
  MarketerMarkers,
  StorePopup,
  MarketerPopup
} from './index';

// Import custom hooks
import {useMapView, useMapControls} from '../../hooks';

export interface StoreMapProps {
  className?: string;
  viewMode?: MapViewMode;
  showStores?: boolean;
  showMarketers?: boolean;
}

export const StoreMapRefactored: React.FC<StoreMapProps> = ({
  className = '',
  viewMode = 'stores',
  showStores = true,
  showMarketers = false
}) => {
  // Store hooks
  const {center, isLoading, setBounds, setCenter} = useMapStore();
  const {stores, selectedStore, setSelectedStore} = useStoreSelectionStore();
  const {marketers, selectedMarketer, setSelectedMarketer} = useMarketerSelectionStore();
  const {filterValidStores, filterValidMarketers} = useCoordinateValidation();
  const {selectMarketer} = useMarketerControl();
  const {user} = useAuth();

  // Determine if marketers data should be fetched
  const shouldFetchMarketers = useMemo(() => {
    return showMarketers || viewMode === 'marketers' || viewMode === 'both';
  }, [showMarketers, viewMode]);

  // Fetch marketers data when marketers should be shown
  useMarketersMap(shouldFetchMarketers);

  // Authorization
  const isProvider = AuthorizationService.getInstance().isServiceProvider(user);

  // Map view logic
  const {viewState, isUpdatingData, handleMoveStart, handleMove, handleMoveEnd} = useMapView({
    center,
    onBoundsUpdate: setBounds,
    onCenterUpdate: setCenter
  });

  // Map controls logic
  const getCurrentLocation = useCallback(async () => {
    // Implementation for getting current location
    // This would typically use the GeolocationService
  }, []);

  const {isLocating, handleMyLocation} = useMapControls({
    onGetCurrentLocation: getCurrentLocation
  });

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    // Zoom logic would be handled by the MapView component
  }, []);

  const handleZoomOut = useCallback(() => {
    // Zoom logic would be handled by the MapView component
  }, []);

  // Event handlers
  const handleStoreSelect = useCallback((store: Store) => {
    setSelectedStore(store);
  }, [setSelectedStore]);

  const handleMarketerSelect = useCallback((marketerId: string) => {
    selectMarketer(marketerId);
  }, [selectMarketer]);

  const handleStorePopupClose = useCallback(() => {
    setSelectedStore(undefined);
  }, [setSelectedStore]);

  const handleMarketerPopupClose = useCallback(() => {
    setSelectedMarketer(undefined);
  }, [setSelectedMarketer]);


  // Get user brands for loading message - handle both User class instance and plain object
  const userBrands = useMemo(() => {
    if (user?.getAssignedBrandNames) {
      return user.getAssignedBrandNames();
    }
    const providerData = (user as { providerData?: { assignedBrands?: Array<{ name: string }> } })?.providerData;
    return providerData?.assignedBrands?.map((b) => b.name) || [];
  }, [user]);

  // Calculate total count for data counter
  const totalCount = useMemo(() => {
    if (showStores && showMarketers) {
      return stores.length + marketers.length;
    }
    return showStores ? stores.length : marketers.length;
  }, [showStores, showMarketers, stores.length, marketers.length]);

  return (
    <MapErrorBoundary className={className}>
      <div className="relative w-full h-[450px] sm:h-[500px] md:h-[550px] lg:h-[600px] xl:h-[650px] rounded-lg overflow-hidden shadow-lg">
        {/* Map Controls */}
        <MapControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onMyLocation={handleMyLocation}
          isLocating={isLocating}
        />

        {/* Data Counter */}
        <MapDataCounter
          count={totalCount}
          type={viewMode === 'both' ? 'both' : viewMode}
          isUpdating={isUpdatingData}
          storesCount={showStores ? stores.length : 0}
          marketersCount={showMarketers ? marketers.length : 0}
        />

        {/* Loading Indicator */}
        <MapLoadingIndicator
          isLoading={isLoading}
          viewMode={viewMode}
          userBrands={userBrands}
          isProvider={isProvider}
        />

        {/* Map View */}
        <MapView
          viewState={viewState}
          onMoveStart={handleMoveStart}
          onMove={handleMove}
          onMoveEnd={handleMoveEnd}
        >
          {/* Store Markers */}
          {showStores && (
            <StoreMarkers
              stores={stores}
              selectedStore={selectedStore}
              onStoreSelect={handleStoreSelect}
              filterValidStores={filterValidStores}
            />
          )}

          {/* Marketer Markers */}
          {showMarketers && (
            <MarketerMarkers
              marketers={marketers}
              selectedMarketer={selectedMarketer}
              onMarketerSelect={handleMarketerSelect}
              filterValidMarketers={filterValidMarketers}
            />
          )}

          {/* Store Popup */}
          {showStores && selectedStore && (
            <StorePopup
              store={selectedStore}
              onClose={handleStorePopupClose}
            />
          )}

          {/* Marketer Popup */}
          {showMarketers && selectedMarketer && (
            <MarketerPopup
              marketer={selectedMarketer}
              onClose={handleMarketerPopupClose}
            />
          )}
        </MapView>
      </div>
    </MapErrorBoundary>
  );
};

export default StoreMapRefactored;
